import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import React, { ReactNode, useEffect, useState } from "react";
import { LinkedIn } from "../features/auth/linkedin";
import Layout from "../layout/layout";
import { LoginPage } from "../pages/auth/login";
import { RegisterPage } from "../pages/auth/register";
import SignUpSetupPage from "../pages/auth/signup";
import { Chat } from "../pages/chat/chat";
import { DashboardPage } from "../pages/dashboard/dashboard";
import History from "../pages/history/history";
import ProfileList from "../pages/profileList/profileList";
import Deals from "../pages/myDeals/deals";
import PublicProfile from "../pages/profile/publicProfile";
import PublicViewProfile from "../pages/profile/publicViewProfile";
import ReadyRoster from "../pages/readyRoster/readyRoster";
import Settings from "../pages/settings/settings";
import { checkAuthentication } from "../services/api";
import Feeds from "../pages/feeds/feeds";
import MarketplaceCards from "../pages/marketPlaceCards/marketPlaceCards";
import Post from "../pages/feeds/post";
import PaymentSuccess from "../components/chat/paymentSuccess";
import AccountCreate from "../pages/stripe/accountCreate";
import ViewDealPage from "../pages/deals/viewDeal/viewDeal";
import Events from "../pages/events/events";
import EventDetail from "../pages/events/eventDetail";
import LoadingScreen from "../components/common/loadingScreen";
import NotificationsPage from "../pages/notification/notificationsPage";
import KnowledgeHub from "../pages/knowledgeHub/knowledgeHub";
import { NotificationsProvider } from "../features/notifications/notificationsProvider";

type RouteProps = {
  children: ReactNode;
};

const ProtectedRoute: React.FC<RouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await checkAuthentication();
        setIsAuthenticated(response === true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<RouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await checkAuthentication();
        setIsAuthenticated(response === true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={location.state?.from || "/dashboard"} replace />;
  }

  return <>{children}</>;
};

const ProtectedLayout: React.FC = () => {
  return (
    <NotificationsProvider>
      <Layout>
        <Outlet />
      </Layout>
    </NotificationsProvider>
  );
};

const Root: React.FC = () => {
  return (
    <Routes>
      <Route path="/view-profile" element={<PublicViewProfile />} />

      <Route
        path="/"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/registration-setup"
        element={
          <PublicRoute>
            <SignUpSetupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/linkedIn"
        element={
          <PublicRoute>
            <LinkedIn />
          </PublicRoute>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/connect/success" element={<AccountCreate />} />

      <Route
        element={
          <ProtectedRoute>
            <ProtectedLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile-list" element={<ProfileList />} />
        <Route path="/readyroster" element={<ReadyRoster />} />
        <Route path="/history" element={<History />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<PublicProfile />} />
        <Route path="/mydeals" element={<Deals />} />
        <Route path="/feeds" element={<Feeds />} />
        <Route path="/marketPlaceCards" element={<MarketplaceCards />} />
        <Route path="/view-post/:postId" element={<Post />} />
        <Route path="/deal/view/:id" element={<ViewDealPage />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/knowledge-hub" element={<KnowledgeHub />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default Root;
